import { c as _c } from "react/compiler-runtime";
import { sendTrackingEvent } from '@/src/lib/tracking/business/send-tracking-event';
import { TrackingEventName } from '@/src/lib/tracking/types';
import { getCurrencyFromItem, getProductPriceFromItem } from '@/src/lib/product/business/product-item';
import { calculateFullScalePrice } from '@/src/lib/product/business/product-prices';
import { useState } from 'react';
import { DeserializedConcreteProduct } from '@/src/lib/catalog-search/types';
import { DeserializedCart } from '@/src/lib/cart/glue/types/deserialized';
import { getMinimumOrderAmount } from '@/src/lib/product/business/product-stock';
import { getSelectedPricingReference } from '@/src/lib/company-user/utils/company-user-utils';
import { useAsyncStartTransition } from '@/src/lib/utils/hooks/use-async-start-transition';
import { DeserializedObject } from '@/src/lib/http/jsonapi/deserializer';
import { CalculatePriceResponse, ConcreteProduct } from '@/src/lib/product/types';
import { useMutation } from '@tanstack/react-query';
import { useSessionGuard } from '@/src/lib/auth/hooks/session-guard';
import { Dictionary } from '@/src/lib/dictionary/types';
import { addToCartAction } from '@/src/lib/cart/business/add-to-cart.action';
export function useAddToCart(product, initialCalculatePrice, addToCart, dict, lang, cart) {
  const $ = _c(28);
  const initialQuantity = initialCalculatePrice.minimumOrderAmountValue || 1;
  const {
    handleProtectedRouteErrors
  } = useSessionGuard(dict);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [quantity, setQuantity] = useState(initialQuantity);
  const [calculatedPrice, setCalculatedPrice] = useState(initialCalculatePrice);
  const asyncStartTransition = useAsyncStartTransition();
  let t0;
  if ($[0] !== product) {
    t0 = getMinimumOrderAmount(product);
    $[0] = product;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const minimumOrderAmountFromProduct = t0;
  let t1;
  if ($[2] !== cart) {
    t1 = getSelectedPricingReference(cart);
    $[2] = cart;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const selectedPricingReference = t1;
  let t2;
  if ($[4] !== addToCart || $[5] !== asyncStartTransition || $[6] !== cart?.currency || $[7] !== lang || $[8] !== product || $[9] !== quantity) {
    t2 = async function performAddToCart() {
      if (!product) {
        throw new Error("Product is not defined");
      }
      sendTrackingEvent(TrackingEventName.ADD_TO_CART, {
        currency: cart?.currency || getCurrencyFromItem(product),
        value: calculateFullScalePrice(quantity * getProductPriceFromItem(product)),
        items: [{
          ...product,
          quantity
        }]
      });
      await asyncStartTransition(async () => {
        const result = await addToCart(quantity, product.sku, lang);
        if (!result.ok && result.error) {
          throw new Error(result.error);
        }
      });
    };
    $[4] = addToCart;
    $[5] = asyncStartTransition;
    $[6] = cart?.currency;
    $[7] = lang;
    $[8] = product;
    $[9] = quantity;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const performAddToCart = t2;
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["addItemToCart"];
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== handleProtectedRouteErrors) {
    t4 = async e => {
      setError(true);
      setTimeout(() => {
        setError(false);
      }, 2000);
      await handleProtectedRouteErrors(e);
    };
    $[12] = handleProtectedRouteErrors;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
    };
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== performAddToCart || $[16] !== t4) {
    t6 = {
      mutationFn: performAddToCart,
      mutationKey: t3,
      onError: t4,
      onSuccess: t5
    };
    $[15] = performAddToCart;
    $[16] = t4;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const {
    isPending: isAddToCartPending,
    mutate: onAddToCartClick
  } = useMutation(t6);
  let t7;
  if ($[18] !== calculatedPrice || $[19] !== error || $[20] !== initialQuantity || $[21] !== isAddToCartPending || $[22] !== minimumOrderAmountFromProduct || $[23] !== onAddToCartClick || $[24] !== quantity || $[25] !== selectedPricingReference || $[26] !== success) {
    t7 = {
      success,
      error,
      isAddToCartPending,
      onAddToCartClick,
      setQuantity,
      quantity,
      initialQuantity,
      calculatedPrice,
      setCalculatedPrice,
      minimumOrderAmountFromProduct,
      selectedPricingReference
    };
    $[18] = calculatedPrice;
    $[19] = error;
    $[20] = initialQuantity;
    $[21] = isAddToCartPending;
    $[22] = minimumOrderAmountFromProduct;
    $[23] = onAddToCartClick;
    $[24] = quantity;
    $[25] = selectedPricingReference;
    $[26] = success;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  return t7;
}